import { useAppSelector } from 'hooks';
import { getLocalTime } from 'utils/functions';

import styles from './MaintenanceMarquee.module.scss';

/**
 * Persistent maintenance banner rendered as a scrolling marquee styled like the
 * app's scrolling notifications (TickerBanner's outlawSolid variant: orange
 * gradient, cream lead icon, dashed rail, continuous scroll) rather than a dull
 * dark bar or a popup. Shown in the lobby; the message announces the server-up
 * time during active maintenance, or the scheduled notice beforehand. Renders
 * nothing when there's no maintenance message.
 *
 * `previewMessage` is the Lobby Lab's (lobbyLabState `maintenanceMarquee`): the
 * bar is invisible outside a maintenance window, so there is otherwise no way
 * to look at it on demand. It fills in ONLY when the store has no message of
 * its own — a real notice always wins, so a switch left on cannot mask one —
 * and it changes nothing else: the lobby's maintenance behaviour keys off the
 * store, not this, so tables stay lit and clickable behind the preview.
 */
const MaintenanceMarquee = ({ previewMessage }: { previewMessage?: string }) => {
  const maintenance = useAppSelector((state) => (state as any)?.maintenanceSchedule);
  const isMaintenance = !!maintenance?.isMaintenance;
  const title = (maintenance?.title || '').trim();
  const displayMessage = (maintenance?.displayMessage || '').trim();

  const stored = isMaintenance
    ? `Maintenance in progress. Server up by ${getLocalTime(
        Number(maintenance.end)
      )}. Kindly finish your game. Thank you for your cooperation.`
    : [title, displayMessage].filter(Boolean).join(' ');
  const message = stored.trim() || (previewMessage ?? '');

  if (!message.trim()) return null;

  const copy = (
    <span className={styles.copy}>
      <span className={styles.dot} aria-hidden='true'>
        •
      </span>
      {message}
    </span>
  );

  return (
    <div className={styles.bar} role='status' aria-label='maintenance notice'>
      <span aria-hidden='true' className={styles.rail} />
      <span className={styles.icon} aria-hidden='true'>
        <svg viewBox='0 0 24 24' width='14' height='14' fill='currentColor'>
          <path d='M12 2a3 3 0 0 0-3 3v1H7.5A1.5 1.5 0 0 0 6 7.5V9H4v2h2v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9h2V9h-2V7.5A1.5 1.5 0 0 0 16.5 6H15V5a3 3 0 0 0-3-3zm-1 4V5a1 1 0 1 1 2 0v1h-2zm-2 6a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm5 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0z' />
        </svg>
      </span>
      <div className={styles.reel}>
        {/* Two identical copies so the marquee loops seamlessly (the second is
            decorative — hidden from assistive tech, which reads the first). */}
        <div className={styles.track}>
          {copy}
          <span aria-hidden='true'>{copy}</span>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMarquee;
